var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PublicIcon from "@mui/icons-material/Public";
import { Alert, Box, Button, Checkbox, Chip, IconButton, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, eachWeekOfInterval, endOfWeek, format, isAfter, isBefore, isSameDay, isWithinInterval, startOfWeek, } from "date-fns";
import Holidays from "date-holidays";
import React from "react";
import { useFormContext } from "react-hook-form";
// Country options for holiday detection
var COUNTRY_OPTIONS = [
    { code: "US", name: "United States" },
    { code: "GB", name: "United Kingdom" },
    { code: "AU", name: "Australia" },
    { code: "CA", name: "Canada" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "ES", name: "Spain" },
    { code: "IT", name: "Italy" },
    { code: "NL", name: "Netherlands" },
    { code: "IE", name: "Ireland" },
    { code: "NZ", name: "New Zealand" },
    { code: "ZA", name: "South Africa" },
    { code: "BR", name: "Brazil" },
    { code: "AR", name: "Argentina" },
    { code: "MX", name: "Mexico" },
];
export var ExclusionsStep = function (_a) {
    var _b, _c, _d;
    var context = _a.context;
    var startDate = context.startDate, endDate = context.endDate, endMode = context.endMode, estimatedEndDate = context.estimatedEndDate, gameWeekStartDay = context.gameWeekStartDay;
    var defaultCountryCode = "US";
    var _e = useFormContext(), watch = _e.watch, setValue = _e.setValue, getValues = _e.getValues;
    // Initialize exclusions defaults if not set
    React.useEffect(function () {
        var current = getValues("exclusions");
        if (!current) {
            setValue("exclusions", {
                excludedDates: [],
                blackoutPeriods: [],
                skipHolidays: true,
                countryCode: defaultCountryCode,
            });
        }
    }, [defaultCountryCode, getValues, setValue]);
    var exclusions = watch("exclusions");
    var excludedDates = (_b = exclusions === null || exclusions === void 0 ? void 0 : exclusions.excludedDates) !== null && _b !== void 0 ? _b : [];
    var blackoutPeriods = (_c = exclusions === null || exclusions === void 0 ? void 0 : exclusions.blackoutPeriods) !== null && _c !== void 0 ? _c : [];
    var countryCode = (_d = exclusions === null || exclusions === void 0 ? void 0 : exclusions.countryCode) !== null && _d !== void 0 ? _d : defaultCountryCode;
    // Helper to update exclusions in form
    var setExcludedDates = function (updater) {
        var newValue = typeof updater === "function" ? updater(excludedDates) : updater;
        setValue("exclusions.excludedDates", newValue);
    };
    var setBlackoutPeriods = function (updater) {
        var newValue = typeof updater === "function" ? updater(blackoutPeriods) : updater;
        setValue("exclusions.blackoutPeriods", newValue);
    };
    var setCountryCode = function (code) {
        setValue("exclusions.countryCode", code);
    };
    // State for adding new items (local only, not persisted until added)
    var _f = __read(React.useState(null), 2), newDate = _f[0], setNewDate = _f[1];
    var _g = __read(React.useState(""), 2), newDateReason = _g[0], setNewDateReason = _g[1];
    var _h = __read(React.useState(null), 2), newBlackoutStart = _h[0], setNewBlackoutStart = _h[1];
    var _j = __read(React.useState(null), 2), newBlackoutEnd = _j[0], setNewBlackoutEnd = _j[1];
    var _k = __read(React.useState(""), 2), newBlackoutReason = _k[0], setNewBlackoutReason = _k[1];
    // Calculate effective end date
    var effectiveEndDate = endMode === "date"
        ? endDate
        : estimatedEndDate || addDays(startDate || new Date(), 180);
    // Detect holidays in the date range
    var detectedHolidays = React.useMemo(function () {
        var e_1, _a;
        if (!startDate || !effectiveEndDate)
            return [];
        try {
            var hd = new Holidays(countryCode);
            var holidays = [];
            // Check each year in the range
            var startYear = startDate.getFullYear();
            var endYear = effectiveEndDate.getFullYear();
            for (var year = startYear; year <= endYear; year++) {
                var yearHolidays = hd.getHolidays(year);
                try {
                    for (var yearHolidays_1 = (e_1 = void 0, __values(yearHolidays)), yearHolidays_1_1 = yearHolidays_1.next(); !yearHolidays_1_1.done; yearHolidays_1_1 = yearHolidays_1.next()) {
                        var holiday = yearHolidays_1_1.value;
                        var holidayDate = new Date(holiday.date);
                        if (isWithinInterval(holidayDate, {
                            start: startDate,
                            end: effectiveEndDate,
                        }) &&
                            (holiday.type === "public" || holiday.type === "bank")) {
                            holidays.push({
                                date: holidayDate,
                                name: holiday.name,
                                type: holiday.type,
                            });
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (yearHolidays_1_1 && !yearHolidays_1_1.done && (_a = yearHolidays_1.return)) _a.call(yearHolidays_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            }
            return holidays.sort(function (a, b) { return a.date.getTime() - b.date.getTime(); });
        }
        catch (_b) {
            return [];
        }
    }, [startDate, effectiveEndDate, countryCode]);
    // Check if a date is already excluded
    var isDateExcluded = function (date) {
        return excludedDates.some(function (ed) { return isSameDay(ed.date, date); });
    };
    // Get exclusion for a date
    var getExclusion = function (date) {
        return excludedDates.find(function (ed) { return isSameDay(ed.date, date); });
    };
    // Toggle holiday exclusion
    var toggleHolidayExclusion = function (holiday) {
        if (isDateExcluded(holiday.date)) {
            setExcludedDates(function (prev) {
                return prev.filter(function (ed) { return !isSameDay(ed.date, holiday.date); });
            });
        }
        else {
            setExcludedDates(function (prev) { return __spreadArray(__spreadArray([], __read(prev), false), [
                {
                    date: holiday.date,
                    reason: holiday.name,
                    isHoliday: true,
                    wholeWeek: false,
                },
            ], false); });
        }
    };
    // Toggle whole week for an exclusion
    var toggleWholeWeek = function (date, wholeWeek) {
        setExcludedDates(function (prev) {
            return prev.map(function (ed) { return (isSameDay(ed.date, date) ? __assign(__assign({}, ed), { wholeWeek: wholeWeek }) : ed); });
        });
    };
    // Add all detected holidays
    var addAllHolidays = function () {
        var newExclusions = detectedHolidays
            .filter(function (h) { return !isDateExcluded(h.date); })
            .map(function (h) { return ({
            date: h.date,
            reason: h.name,
            isHoliday: true,
            wholeWeek: false,
        }); });
        setExcludedDates(function (prev) { return __spreadArray(__spreadArray([], __read(prev), false), __read(newExclusions), false); });
    };
    // Remove all holiday exclusions
    var removeAllHolidays = function () {
        setExcludedDates(function (prev) { return prev.filter(function (ed) { return !ed.isHoliday; }); });
    };
    // Add manual date exclusion
    var addManualDate = function () {
        if (!newDate)
            return;
        if (isDateExcluded(newDate))
            return;
        setExcludedDates(function (prev) { return __spreadArray(__spreadArray([], __read(prev), false), [
            {
                date: newDate,
                reason: newDateReason || "Manual exclusion",
                isHoliday: false,
                wholeWeek: false,
            },
        ], false); });
        setNewDate(null);
        setNewDateReason("");
    };
    // Remove date exclusion
    var removeDate = function (date) {
        setExcludedDates(function (prev) { return prev.filter(function (ed) { return !isSameDay(ed.date, date); }); });
    };
    // Add blackout period
    var addBlackoutPeriod = function () {
        if (!newBlackoutStart || !newBlackoutEnd)
            return;
        if (!isAfter(newBlackoutEnd, newBlackoutStart))
            return;
        setBlackoutPeriods(function (prev) { return __spreadArray(__spreadArray([], __read(prev), false), [
            {
                startDate: newBlackoutStart,
                endDate: newBlackoutEnd,
                reason: newBlackoutReason || "Blackout period",
            },
        ], false); });
        setNewBlackoutStart(null);
        setNewBlackoutEnd(null);
        setNewBlackoutReason("");
    };
    // Remove blackout period
    var removeBlackoutPeriod = function (index) {
        setBlackoutPeriods(function (prev) { return prev.filter(function (_, i) { return i !== index; }); });
    };
    // Calculate total excluded weeks
    var excludedWeeksCount = React.useMemo(function () {
        if (!startDate || !effectiveEndDate)
            return 0;
        var weeks = eachWeekOfInterval({ start: startDate, end: effectiveEndDate }, { weekStartsOn: gameWeekStartDay });
        return weeks.filter(function (weekStart) {
            var weekEnd = addDays(weekStart, 6);
            // Check if any whole-week exclusion covers this week
            var hasWholeWeekExclusion = excludedDates.some(function (ed) {
                if (!ed.wholeWeek)
                    return false;
                var edWeekStart = startOfWeek(ed.date, {
                    weekStartsOn: gameWeekStartDay,
                });
                return isSameDay(edWeekStart, weekStart);
            });
            if (hasWholeWeekExclusion)
                return true;
            // Check if any single-date exclusion falls in this week
            var hasExcludedDate = excludedDates.some(function (ed) {
                return !ed.wholeWeek &&
                    isWithinInterval(ed.date, { start: weekStart, end: weekEnd });
            });
            // Check if week overlaps with any blackout period
            var inBlackout = blackoutPeriods.some(function (bp) {
                return isWithinInterval(weekStart, {
                    start: bp.startDate,
                    end: bp.endDate,
                }) ||
                    isWithinInterval(weekEnd, { start: bp.startDate, end: bp.endDate }) ||
                    (isBefore(weekStart, bp.startDate) && isAfter(weekEnd, bp.endDate));
            });
            return hasExcludedDate || inBlackout;
        }).length;
    }, [startDate, effectiveEndDate, excludedDates, blackoutPeriods]);
    var hasDateRange = startDate && effectiveEndDate;
    return (_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, children: _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "Specify dates when matches should not be scheduled." }), !hasDateRange && (_jsx(Alert, { severity: "info", sx: { mb: 3 }, children: "Set a season date range in the previous step to detect holidays." })), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(PublicIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Public Holidays" })] }), _jsxs(Box, { sx: { mb: 2 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "Country" }), _jsx(Stack, { direction: "row", spacing: 1, sx: { flexWrap: "wrap", gap: 1 }, children: COUNTRY_OPTIONS.map(function (country) { return (_jsx(Chip, { label: country.name, size: "small", color: countryCode === country.code ? "primary" : "default", variant: countryCode === country.code ? "filled" : "outlined", onClick: function () { return setCountryCode(country.code); } }, country.code)); }) })] }), hasDateRange && detectedHolidays.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: "row", spacing: 1, sx: { mb: 2 }, children: [_jsx(Button, { size: "small", variant: "outlined", onClick: addAllHolidays, children: "Add All" }), _jsx(Button, { size: "small", variant: "outlined", onClick: removeAllHolidays, children: "Remove All" })] }), _jsx(List, { dense: true, disablePadding: true, children: detectedHolidays.map(function (holiday, index) {
                                        var exclusion = getExclusion(holiday.date);
                                        var isExcluded = !!exclusion;
                                        var weekStart = startOfWeek(holiday.date, {
                                            weekStartsOn: gameWeekStartDay,
                                        });
                                        var weekEndDate = endOfWeek(holiday.date, {
                                            weekStartsOn: gameWeekStartDay,
                                        });
                                        return (_jsxs(ListItem, { sx: {
                                                bgcolor: isExcluded ? "action.selected" : "transparent",
                                                borderRadius: 1,
                                                mb: 0.5,
                                                flexWrap: "wrap",
                                            }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(Checkbox, { edge: "start", size: "small", checked: isExcluded, onChange: function () { return toggleHolidayExclusion(holiday); } }) }), _jsx(ListItemText, { primary: holiday.name, secondary: format(holiday.date, "EEEE, MMMM d, yyyy"), sx: { flexGrow: 1, minWidth: 200 } }), isExcluded && (_jsxs(ToggleButtonGroup, { size: "small", exclusive: true, value: exclusion.wholeWeek ? "week" : "date", onChange: function (_, val) {
                                                        if (val)
                                                            toggleWholeWeek(holiday.date, val === "week");
                                                    }, sx: { ml: 1 }, children: [_jsx(ToggleButton, { value: "date", sx: { py: 0.25, px: 1.5 }, children: _jsx(Typography, { variant: "caption", children: "Date only" }) }), _jsx(ToggleButton, { value: "week", sx: { py: 0.25, px: 1.5 }, children: _jsxs(Typography, { variant: "caption", children: ["Week (", format(weekStart, "MMM d"), "\u2013", format(weekEndDate, "d"), ")"] }) })] }))] }, index));
                                    }) })] })), hasDateRange && detectedHolidays.length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No public holidays found in the selected date range." }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(CalendarMonthIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Specific Dates" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Add individual dates to skip (e.g., club events, tournaments)." }), _jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, sx: { mb: 2 }, children: [_jsx(DatePicker, { label: "Date to exclude", value: newDate, onChange: setNewDate, minDate: startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 180 } },
                                    } }), _jsx(TextField, { size: "small", label: "Reason (optional)", value: newDateReason, onChange: function (e) { return setNewDateReason(e.target.value); }, sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: addManualDate, disabled: !newDate || isDateExcluded(newDate), children: "Add" })] }), excludedDates.filter(function (ed) { return !ed.isHoliday; }).length > 0 && (_jsx(List, { dense: true, children: excludedDates
                                .filter(function (ed) { return !ed.isHoliday; })
                                .sort(function (a, b) { return a.date.getTime() - b.date.getTime(); })
                                .map(function (exclusion, index) { return (_jsxs(ListItem, { sx: { bgcolor: "action.hover", borderRadius: 1, mb: 0.5 }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(EventBusyIcon, { fontSize: "small", color: "error" }) }), _jsx(ListItemText, { primary: format(exclusion.date, "EEEE, MMMM d, yyyy"), secondary: exclusion.reason }), _jsx(ListItemSecondaryAction, { children: _jsx(IconButton, { edge: "end", size: "small", onClick: function () { return removeDate(exclusion.date); }, children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }, index)); }) }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { mb: 2 }, children: [_jsx(EventBusyIcon, { color: "primary" }), _jsx(Typography, { variant: "subtitle1", fontWeight: 600, children: "Blackout Periods" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Add date ranges when no matches should be scheduled (e.g., Christmas break, school holidays)." }), _jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, sx: { mb: 2 }, children: [_jsx(DatePicker, { label: "Start date", value: newBlackoutStart, onChange: setNewBlackoutStart, minDate: startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 160 } },
                                    } }), _jsx(DatePicker, { label: "End date", value: newBlackoutEnd, onChange: setNewBlackoutEnd, minDate: newBlackoutStart || startDate || undefined, maxDate: effectiveEndDate || undefined, slotProps: {
                                        textField: { size: "small", sx: { minWidth: 160 } },
                                    } }), _jsx(TextField, { size: "small", label: "Reason (optional)", value: newBlackoutReason, onChange: function (e) { return setNewBlackoutReason(e.target.value); }, sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: addBlackoutPeriod, disabled: !newBlackoutStart ||
                                        !newBlackoutEnd ||
                                        !isAfter(newBlackoutEnd, newBlackoutStart), children: "Add" })] }), blackoutPeriods.length > 0 && (_jsx(List, { dense: true, children: blackoutPeriods.map(function (period, index) { return (_jsxs(ListItem, { sx: { bgcolor: "error.lighter", borderRadius: 1, mb: 0.5 }, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: _jsx(EventBusyIcon, { fontSize: "small", color: "error" }) }), _jsx(ListItemText, { primary: "".concat(format(period.startDate, "MMM d"), " \u2013 ").concat(format(period.endDate, "MMM d, yyyy")), secondary: period.reason }), _jsx(ListItemSecondaryAction, { children: _jsx(IconButton, { edge: "end", size: "small", onClick: function () { return removeBlackoutPeriod(index); }, children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }, index)); }) }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, bgcolor: "action.hover", borderRadius: 2 }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }, children: "Exclusions Summary" }), _jsxs(Stack, { direction: "row", spacing: 4, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: excludedDates.filter(function (ed) { return ed.isHoliday; }).length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "holidays" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: excludedDates.filter(function (ed) { return !ed.isHoliday; }).length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "manual dates" })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h5", fontWeight: 700, color: "primary", children: blackoutPeriods.length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "blackout periods" })] }), _jsxs(Box, { children: [_jsxs(Typography, { variant: "h5", fontWeight: 700, color: "warning.main", children: ["~", excludedWeeksCount] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "weeks affected" })] })] })] })] }) }));
};
